import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Shield, Loader2, Plus, Edit, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp, Euro, RefreshCw
} from 'lucide-react'
import { toast } from 'sonner'
import ReglesConservation from '../../../components/conservation/ReglesConservation'
import ImportProvisions from '../../../components/conservation/ImportProvisions'

const API = process.env.REACT_APP_BACKEND_URL

const fmt = (v) => {
  if (v == null) return '--'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

const pct = (v) => v != null ? `${v.toFixed(1)}%` : '--'

const MODE_GESTION_LABELS = {
  assurance: { label: 'Assurance', color: 'bg-blue-100 text-blue-700', icon: Shield },
  amiable: { label: 'Amiable', color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
  retention: { label: 'Rétention', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
}

const MOTIF_LABELS = {
  amiable: 'Amiable (hors assurance)',
  retention: 'Rétention (auto-assuré)',
  non_responsable: 'Non responsable',
}

export default function ConservationBudgetTab({ tenantId }) {
  const [budget, setBudget] = useState(null)
  const [loading, setLoading] = useState(true)
  const [annee, setAnnee] = useState(null)
  const [exercices, setExercices] = useState([])

  useEffect(() => {
    fetch(`${API}/api/conservation/${tenantId}/exercices`)
      .then(r => r.json())
      .then(d => { setExercices(d.exercices || []); setAnnee(d.annee_courante || new Date().getFullYear()) })
      .catch(() => setAnnee(new Date().getFullYear()))
  }, [tenantId])
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ montant_initial: '', notes: '', franchise_rc_materiel: '', franchise_rc_corporel: '', franchise_dta: '' })
  const [synthese, setSynthese] = useState(null)
  const [showConsommations, setShowConsommations] = useState(false)
  const [recalculating, setRecalculating] = useState(false)

  const fetchBudget = useCallback(async () => {
    if (!annee) return
    setLoading(true)
    try {
      const r = await fetch(`${API}/api/conservation/${tenantId}/${annee}`)
      const d = await r.json()
      setBudget(d.budget || null)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [tenantId, annee])

  const fetchSynthese = useCallback(async () => {
    if (!annee) return
    try {
      const r = await fetch(`${API}/api/synthese-amiable/${tenantId}/${annee}`)
      const d = await r.json()
      setSynthese(d)
    } catch (e) { console.error(e) }
  }, [tenantId, annee])

  useEffect(() => { fetchBudget(); fetchSynthese() }, [fetchBudget, fetchSynthese])

  const handleSaveBudget = async (e) => {
    e.preventDefault()
    if (!form.montant_initial) { toast.error('Montant requis'); return }
    setSaving(true)
    try {
      const r = await fetch(`${API}/api/conservation/${tenantId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          annee,
          montant_initial: parseFloat(form.montant_initial),
          notes: form.notes || null,
          franchise_rc_materiel: form.franchise_rc_materiel ? parseFloat(form.franchise_rc_materiel) : null,
          franchise_rc_corporel: form.franchise_rc_corporel ? parseFloat(form.franchise_rc_corporel) : null,
          franchise_dta: form.franchise_dta ? parseFloat(form.franchise_dta) : null,
        }),
      })
      if (!r.ok) throw new Error('Erreur serveur')
      toast.success(budget ? 'Budget mis à jour' : 'Budget créé')
      setShowForm(false)
      fetchBudget()
    } catch (e) { toast.error(e.message) }
    finally { setSaving(false) }
  }

  const handleRecalculer = async () => {
    setRecalculating(true)
    try {
      const r = await fetch(`${API}/api/conservation/${tenantId}/recalculer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ annee }),
      })
      if (!r.ok) throw new Error('Erreur recalcul')
      toast.success('Consommations recalculées')
      fetchBudget()
    } catch (e) { toast.error(e.message) }
    finally { setRecalculating(false) }
  }

  const startEdit = () => {
    setForm({
      montant_initial: budget?.montant_initial || '',
      notes: budget?.notes || '',
      franchise_rc_materiel: budget?.franchise_rc_materiel || '',
      franchise_rc_corporel: budget?.franchise_rc_corporel || '',
      franchise_dta: budget?.franchise_dta || '',
    })
    setShowForm(true)
  }

  const consommations = budget?.consommations || []
  const consommees = consommations.filter(c => c.est_consomme)
  const nonConsommees = consommations.filter(c => !c.est_consomme)

  // Gauge percentage
  const tauxConsommation = budget?.taux_consommation ?? 0
  const gaugeColor = tauxConsommation > 80 ? 'text-red-500' : tauxConsommation > 50 ? 'text-amber-500' : 'text-emerald-500'
  const gaugeBg = tauxConsommation > 80 ? 'bg-red-500' : tauxConsommation > 50 ? 'bg-amber-500' : 'bg-emerald-500'

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6" data-testid="conservation-budget-tab">
      {/* Header with year selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800">Budget de Conservation</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Suivi des franchises payées sur sinistres déclarés à l'assurance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={annee || ''}
            onChange={e => setAnnee(Number(e.target.value))}
            className="h-9 px-3 text-sm font-semibold text-slate-800 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="conservation-exercice-select"
          >
            {exercices.length > 0 ? exercices.map(ex => (
              <option key={ex.annee} value={ex.annee}>
                Exercice {ex.label}{ex.en_cours ? ' (en cours)' : ex.futur ? ' (à venir)' : ''}
              </option>
            )) : <option value={annee || ''}>{annee}</option>}
          </select>
          {budget ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleRecalculer}
                disabled={recalculating}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-amber-200 text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 disabled:opacity-50"
                data-testid="recalculer-btn"
              >
                {recalculating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw size={13} />} Recalculer
              </button>
              <button
                onClick={startEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                data-testid="edit-budget-btn"
              >
                <Edit size={13} /> Modifier
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setForm({ montant_initial: '', notes: '', franchise_rc_materiel: '', franchise_rc_corporel: '', franchise_dta: '' }); setShowForm(true) }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700"
              data-testid="create-budget-btn"
            >
              <Plus size={14} /> Définir le budget
            </button>
          )}
        </div>
      </div>

      {/* Budget form */}
      {showForm && (
        <div className="bg-white border border-indigo-200 rounded-xl p-5" data-testid="conservation-form">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            {budget ? `Modifier le budget ${annee}` : `Créer le budget ${annee}`}
          </h3>
          <form onSubmit={handleSaveBudget} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Montant initial *</label>
                <input
                  type="number" step="0.01" required
                  value={form.montant_initial}
                  onChange={e => setForm({ ...form, montant_initial: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="900000"
                  data-testid="conservation-montant-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="Budget conservation 2026..."
                />
              </div>
            </div>

            {/* Franchise par nature */}
            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">Franchises par nature (plafond par sinistre)</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">RC Materiel</label>
                  <input
                    type="number" step="0.01"
                    value={form.franchise_rc_materiel}
                    onChange={e => setForm({ ...form, franchise_rc_materiel: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="50000"
                    data-testid="franchise-rc-materiel-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">RC Corporel</label>
                  <input
                    type="number" step="0.01"
                    value={form.franchise_rc_corporel}
                    onChange={e => setForm({ ...form, franchise_rc_corporel: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="50000"
                    data-testid="franchise-rc-corporel-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">DTA</label>
                  <input
                    type="number" step="0.01"
                    value={form.franchise_dta}
                    onChange={e => setForm({ ...form, franchise_dta: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="0"
                    data-testid="franchise-dta-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Sinistres responsables ou 50/50 : la consommation sera plafonnee au montant de la franchise correspondante.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg">
                Annuler
              </button>
              <button
                type="submit" disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid="conservation-save-btn"
              >
                {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}

      {/* No budget state */}
      {!budget && !showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="no-budget">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600 font-medium">Aucun budget de conservation pour {annee}</p>
          <p className="text-xs text-slate-400 mt-1">
            Définissez le budget de conservation (franchise totale acceptée) pour suivre la consommation des sinistres déclarés à l'assurance.
          </p>
        </div>
      )}

      {/* Budget KPI Cards */}
      {budget && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="kpi-montant-initial">
              <p className="text-xs text-slate-500 mb-1">Budget initial</p>
              <p className="text-xl font-bold text-slate-800">{fmt(budget.montant_initial)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="kpi-consomme">
              <p className="text-xs text-slate-500 mb-1">Consommé</p>
              <p className="text-xl font-bold text-red-600">{fmt(budget.consomme)}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{consommees.length} sinistre(s)</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="kpi-reste">
              <p className="text-xs text-slate-500 mb-1">Restant</p>
              <p className={`text-xl font-bold ${budget.reste < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(budget.reste)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="kpi-taux">
              <p className="text-xs text-slate-500 mb-1">Taux de consommation</p>
              <p className={`text-xl font-bold ${gaugeColor}`}>{pct(tauxConsommation)}</p>
              <div className="w-full bg-slate-100 rounded-full h-2 mt-2">
                <div className={`h-2 rounded-full transition-all ${gaugeBg}`} style={{ width: `${Math.min(tauxConsommation, 100)}%` }} />
              </div>
            </div>
          </div>

          {/* Franchise par nature display */}
          {(budget.franchise_rc_materiel || budget.franchise_rc_corporel || budget.franchise_dta) && (
            <div className="flex items-center gap-4 px-1">
              {budget.franchise_rc_materiel > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="franchise-rc-mat-display">
                  <span className="w-2 h-2 rounded-full bg-blue-400" />
                  Franchise RC Mat : <span className="font-semibold text-slate-700">{fmt(budget.franchise_rc_materiel)}</span>
                </div>
              )}
              {budget.franchise_rc_corporel > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="franchise-rc-corp-display">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  Franchise RC Corpo : <span className="font-semibold text-slate-700">{fmt(budget.franchise_rc_corporel)}</span>
                </div>
              )}
              {budget.franchise_dta > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="franchise-dta-display">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  Franchise DTA : <span className="font-semibold text-slate-700">{fmt(budget.franchise_dta)}</span>
                </div>
              )}
            </div>
          )}

          {/* Non-consumed info */}
          {nonConsommees.length > 0 && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
              <p className="text-xs font-medium text-teal-700 mb-1">
                {nonConsommees.length} sinistre(s) ne consommant pas le budget
              </p>
              <div className="text-xs text-teal-600 flex flex-wrap gap-x-6 gap-y-1" data-testid="non-consomme-detail">
                {(budget.non_consomme_detail?.amiable?.nb || 0) > 0 && (
                  <span><strong>{budget.non_consomme_detail.amiable.nb}</strong> amiable(s) — franchises évitées : <strong>{fmt(budget.non_consomme_detail.amiable.franchise_evitee)}</strong> (plafonnées au forfait convention)</span>
                )}
                {(budget.non_consomme_detail?.non_responsable?.nb || 0) > 0 && (
                  <span><strong>{budget.non_consomme_detail.non_responsable.nb}</strong> non responsable(s) — aucune franchise due (recours compagnie)</span>
                )}
                {(budget.non_consomme_detail?.retention?.nb || 0) > 0 && (
                  <span><strong>{budget.non_consomme_detail.retention.nb}</strong> en rétention</span>
                )}
              </div>
            </div>
          )}

          {/* Consommations detail */}
          {consommations.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowConsommations(!showConsommations)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
                data-testid="toggle-consommations"
              >
                <h3 className="text-sm font-semibold text-slate-700">
                  Détail des consommations ({consommations.length})
                </h3>
                {showConsommations ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
              </button>
              {showConsommations && (
                <div className="border-t border-slate-100">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-slate-500 font-medium">Sinistre</th>
                        <th className="px-4 py-2 text-left text-slate-500 font-medium">Franchise</th>
                        <th className="px-4 py-2 text-left text-slate-500 font-medium">Consommé ?</th>
                        <th className="px-4 py-2 text-left text-slate-500 font-medium">Motif</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {consommations.map(c => (
                        <tr key={c.id} className="hover:bg-slate-50" data-testid={`consommation-row-${c.id}`}>
                          <td className="px-4 py-2.5 text-slate-700 font-mono">{c.sinistre_id?.substring(0, 8)}...</td>
                          <td className="px-4 py-2.5 text-slate-700 font-medium">{fmt(c.montant_franchise_appliquee)}</td>
                          <td className="px-4 py-2.5">
                            {c.est_consomme ? (
                              <span className="inline-flex items-center gap-1 text-red-600">
                                <XCircle size={12} /> Oui
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-emerald-600">
                                <CheckCircle size={12} /> Non
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-slate-500">
                            {c.motif_non_consommation ? MOTIF_LABELS[c.motif_non_consommation] || c.motif_non_consommation : '--'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Budget notes */}
          {budget.notes && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-500 mb-1">Notes</p>
              <p className="text-sm text-slate-700">{budget.notes}</p>
            </div>
          )}
        </>
      )}

      {/* Synthèse amiable */}
      {synthese && (synthese.nb_amiables > 0 || synthese.nb_retention > 0) && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="synthese-amiable">
          <div className="px-5 py-3 border-b border-slate-100 bg-teal-50">
            <h3 className="text-sm font-semibold text-teal-800">Synthèse amiable & rétention — {annee}</h3>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <p className="text-xs text-slate-500">Sinistres amiables</p>
                <p className="text-lg font-bold text-teal-700">{synthese.nb_amiables}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Coût amiable HT</p>
                <p className="text-lg font-bold text-teal-700" data-testid="synthese-cout-amiable">{fmt(synthese.total_cout_amiable)}</p>
                <p className="text-[10px] text-slate-400">Somme des devis HT</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Économie amiable totale</p>
                <p className="text-lg font-bold text-emerald-600">{fmt(synthese.total_economie_amiable)}</p>
                <p className="text-[10px] text-slate-400">vs forfaits IRSA/IRCA</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Sinistres en rétention</p>
                <p className="text-lg font-bold text-amber-700">{synthese.nb_retention}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Coût rétention</p>
                <p className="text-lg font-bold text-amber-600">{fmt(synthese.total_retention)}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500">
              <span>Total sinistres : {synthese.nb_sinistres_total}</span>
              <span>Assurance : {synthese.nb_assurance}</span>
              <span>Amiable : {synthese.nb_amiables}</span>
              <span>Rétention : {synthese.nb_retention}</span>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-slate-600 mb-2">Légende des modes de gestion</p>
        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            <div>
              <p className="text-xs font-medium text-slate-700">Assurance (Conservation)</p>
              <p className="text-[10px] text-slate-500">Franchise payée = consomme le budget</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-teal-500" />
            <div>
              <p className="text-xs font-medium text-slate-700">Amiable</p>
              <p className="text-[10px] text-slate-500">Hors assurance, économie vs IRSA</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
            <div>
              <p className="text-xs font-medium text-slate-700">Rétention</p>
              <p className="text-[10px] text-slate-500">Auto-assuré, hors budget</p>
            </div>
          </div>
        </div>
      </div>

      {/* Regles de conservation parametrables */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mt-6">
        <ReglesConservation tenantId={tenantId} />
      </div>

      {/* Import provisions compagnie */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mt-6">
        <ImportProvisions tenantId={tenantId} />
      </div>
    </div>
  )
}
